'use client'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { type TransferDirection, useInternalTransfer } from '@/hooks/useInternalTransfer'
import { CHAIN_CONFIG, getExplorerUrl } from '@/lib/api/relay'
import { useTrading } from '@/providers/TradingProvider'
import { useModalStore } from '@/stores/modal-store'
import { AlertCircle, ArrowDown, CheckCircle2, ExternalLink, Info, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'

type Platform = 'polymarket' | 'kalshi'

export function BridgeModal() {
	const t = useTranslations('bridge')
	const tCommon = useTranslations('common')
	const { activeModal, closeModal } = useModalStore()
	const isOpen = activeModal === 'bridge'

	const { relayClient, isReady } = useTrading()

	const {
		polymarketBalance,
		kalshiBalance,
		quote,
		fetchQuote,
		execute,
		status,
		error,
		txHash,
		reset,
	} = useInternalTransfer()

	const [fromPlatform, setFromPlatform] = useState<Platform>('polymarket')
	const [amount, setAmount] = useState('')
	const [isMaxAmount, setIsMaxAmount] = useState(false)

	const direction: TransferDirection = fromPlatform === 'polymarket' ? 'toKalshi' : 'toPolymarket'
	const toPlatform: Platform = fromPlatform === 'polymarket' ? 'kalshi' : 'polymarket'

	const fromBalance = fromPlatform === 'polymarket' ? polymarketBalance : kalshiBalance
	const toBalance = fromPlatform === 'polymarket' ? kalshiBalance : polymarketBalance

	const amountNum = Number.parseFloat(amount) || 0
	const isValidAmount = amountNum > 0 && (isMaxAmount || amountNum <= fromBalance)

	// Reset form when modal closes
	useEffect(() => {
		if (!isOpen) {
			setAmount('')
			setFromPlatform('polymarket')
			setIsMaxAmount(false)
			reset()
		}
	}, [isOpen, reset])

	const handleMax = useCallback(() => {
		setAmount(fromBalance.toFixed(2))
		setIsMaxAmount(true)
	}, [fromBalance])

	const handleSwapPlatforms = useCallback(() => {
		setFromPlatform(toPlatform)
		setAmount('')
		setIsMaxAmount(false)
		reset()
	}, [toPlatform, reset])

	const handleGetQuote = useCallback(async () => {
		if (!isValidAmount) {
			return
		}
		await fetchQuote({
			amount,
			direction,
			useMaxBalance: isMaxAmount,
		})
	}, [amount, direction, isValidAmount, fetchQuote, isMaxAmount])

	const handleConfirm = useCallback(async () => {
		if (direction === 'toKalshi' && !isReady) {
			return
		}
		await execute(relayClient)
	}, [execute, relayClient, direction, isReady])

	const handleClose = useCallback(() => {
		if (status === 'executing') {
			return
		}
		closeModal()
	}, [status, closeModal])

	const formatBalance = (value: number) => {
		return value.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})
	}

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
			<DialogContent
				closeButtonContainerClasses="top-[14px] size-[26px]"
				className="gap-[24px] rounded-3xl border border-white/10 bg-brand-dark-light p-[16px] sm:max-w-[512px]"
			>
				<DialogHeader>
					<DialogTitle className="text-center text-[18px] text-white leading-[100%]">
						{t('title')}
					</DialogTitle>
				</DialogHeader>

				{status === 'idle' || status === 'loading_quote' || status === 'error' ? (
					<div className="flex flex-col gap-[24px]">
						{/* From Section */}
						<div className="flex flex-col gap-[12px]">
							<div className="flex flex-col gap-[12px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
								<div className="flex items-center justify-between">
									<p className=" text-[16px] text-white/70 leading-[100%]">{t('from')}</p>
									<Select
										value={fromPlatform}
										onValueChange={val => {
											setFromPlatform(val as Platform)
											setAmount('')
										}}
									>
										<SelectTrigger className="!bg-transparent h-auto w-auto gap-[8px] border-0 p-0 text-[14px] text-white outline-0">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="polymarket">
												<div className="flex items-center gap-2">
													<img
														src="/logos/polymarket.jpeg"
														alt="Polymarket"
														width={16}
														height={16}
														className="rounded-[4px]"
													/>
													Polymarket
												</div>
											</SelectItem>
											<SelectItem value="kalshi">
												<div className="flex items-center gap-2">
													<img
														src="/logos/kalshi.jpeg"
														alt="Kalshi"
														width={16}
														height={16}
														className="rounded-[4px]"
													/>
													Kalshi
												</div>
											</SelectItem>
										</SelectContent>
									</Select>
								</div>

								<div className="flex items-center justify-between gap-[12px]">
									<div className="flex items-center gap-[8px] overflow-hidden">
										<input
											type="text"
											inputMode="decimal"
											value={amount}
											onChange={e => {
												const value = e.target.value
												if (value === '' || /^\d*\.?\d*$/.test(value)) {
													setAmount(value)
													setIsMaxAmount(false)
												}
											}}
											placeholder="0"
											className="w-full bg-transparent text-[48px] text-white leading-[100%] [appearance:textfield] placeholder:text-white/30 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
										/>
										<span className="shrink-0 font-medium text-[24px] text-white/50 leading-[100%] tracking-[0.25px]">
											USDC
										</span>
									</div>
									<button
										type="button"
										onClick={handleMax}
										className="shrink-0 rounded-[12px] bg-white/5 px-[16px] py-[10px] text-[14px] text-white leading-[100%] transition-colors hover:bg-white/20"
									>
										{t('max')}
									</button>
								</div>

								<div className="flex items-center justify-between">
									<p className=" text-[14px] text-white/75 leading-[100%]">{t('balance')}</p>
									<p className=" text-[14px] text-white/75 leading-[100%]">
										{formatBalance(fromBalance)} USDC
									</p>
								</div>

								{!isMaxAmount && amountNum > fromBalance && (
									<p className="text-red-500 text-xs">{t('insufficientBalance')}</p>
								)}
							</div>

							{/* Swap Arrow */}
							<div className="relative flex justify-center">
								<button
									type="button"
									onClick={handleSwapPlatforms}
									className="-top-[24px] absolute z-10 flex h-[40px] w-[40px] items-center justify-center rounded-full border-[3px] border-brand-gray bg-brand-dark-light transition-colors hover:bg-white/10"
								>
									<ArrowDown className="h-5 w-5 text-white" />
								</button>
							</div>

							{/* To Section */}
							<div className="flex flex-col gap-[12px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
								<div className="flex items-center justify-between">
									<p className=" text-[16px] text-white/70 leading-[100%]">{t('to')}</p>
									<div className="flex items-center gap-[8px]">
										<img
											src={
												toPlatform === 'kalshi' ? '/logos/kalshi.jpeg' : '/logos/polymarket.jpeg'
											}
											alt={toPlatform}
											width={16}
											height={16}
											className="rounded-[4px]"
										/>
										<span className=" text-[14px] text-white capitalize">
											{toPlatform === 'kalshi' ? 'Kalshi' : 'Polymarket'}
										</span>
									</div>
								</div>

								<div className="flex items-center justify-between gap-[12px]">
									<p className=" text-[48px] text-white leading-[100%]">{amount || '0'}</p>
									<span className="shrink-0 font-medium text-[24px] text-white/50 leading-[100%] tracking-[0.25px]">
										USDC
									</span>
								</div>

								<div className="flex items-center justify-between">
									<p className=" text-[14px] text-white/75 leading-[100%]">{t('balance')}</p>
									<p className=" text-[14px] text-white/75 leading-[100%]">
										{formatBalance(toBalance)} USDC
									</p>
								</div>
							</div>
						</div>

						{/* Error */}
						{error && (
							<div className="flex items-center gap-[12px] rounded-[12px] border border-red-500/20 bg-red-500/10 p-[16px]">
								<AlertCircle className="h-5 w-5 shrink-0 text-red-400" />
								<p className=" text-[14px] text-red-400 leading-[140%]">{error}</p>
							</div>
						)}

						{/* Info Banner */}
						<div className="flex items-center gap-[12px] rounded-[12px] bg-[#2B2117] p-[16px]">
							<Info className="h-5 w-5 shrink-0 text-[#D79B28]" />
							<p className=" text-[#D79B28] text-[14px] leading-[140%]">{t('transferInfo')}</p>
						</div>

						{/* Bridge Button */}
						<Button
							onClick={handleGetQuote}
							disabled={!isValidAmount || status === 'loading_quote'}
							className="h-12 w-full rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90 disabled:opacity-50"
						>
							{status === 'loading_quote' ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									{t('gettingQuote')}
								</>
							) : (
								t('continue')
							)}
						</Button>
					</div>
				) : status === 'quote_ready' && quote ? (
					/* Quote Confirmation View */
					<div className="flex flex-col gap-[24px]">
						<div className="flex flex-col gap-[12px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
							<div className="flex items-center justify-between">
								<div className="text-center">
									<p className=" text-[14px] text-white/70 leading-[100%]">
										{fromPlatform === 'polymarket' ? 'Polymarket' : 'Kalshi'}
									</p>
									<p className="mt-[8px] font-bold text-[24px] text-white leading-[100%]">
										-${quote.inputAmount}
									</p>
								</div>
								<ArrowDown className="-rotate-90 h-5 w-5 text-brand-blue" />
								<div className="text-center">
									<p className=" text-[14px] text-white/70 leading-[100%]">
										{toPlatform === 'kalshi' ? 'Kalshi' : 'Polymarket'}
									</p>
									<p className="mt-[8px] font-bold text-[24px] text-emerald-400 leading-[100%]">
										+${quote.estimatedOutput}
									</p>
								</div>
							</div>
						</div>
						{/* Fee Details */}
						<div className="flex flex-col gap-[12px]">
							<div className="flex items-center justify-between">
								<p className=" text-[14px] text-white/75 leading-[100%]">{t('networkFee')}</p>
								<p className=" text-[14px] text-emerald-400 leading-[100%]">{t('sponsored')}</p>
							</div>
							<div className="flex items-center justify-between">
								<p className=" text-[14px] text-white/75 leading-[100%]">{t('relayFee')}</p>
								<p className=" text-[14px] text-white leading-[100%]">${quote.totalFeeUsd}</p>
							</div>
							<div className="flex items-center justify-between">
								<p className=" text-[14px] text-white/75 leading-[100%]">{t('estTime')}</p>
								<p className=" text-[14px] text-white leading-[100%]">~{quote.timeEstimate}s</p>
							</div>
						</div>

						{/* Action Buttons */}
						<div className="flex gap-[12px]">
							<Button
								variant="outline"
								onClick={reset}
								className="h-12 flex-1 rounded-[12px] border-white/10 text-white hover:bg-white/5"
							>
								{tCommon('back')}
							</Button>
							<Button
								onClick={handleConfirm}
								className="h-12 flex-1 rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90"
							>
								{t('confirmBridge')}
							</Button>
						</div>
					</div>
				) : status === 'executing' ? (
					/* Executing View */
					<div className="flex flex-col items-center gap-[16px] py-[32px]">
						<Loader2 className="h-12 w-12 animate-spin text-brand-blue" />
						<div className="text-center">
							<p className=" font-medium text-[18px] text-white leading-[100%]">
								{t('processingBridge')}
							</p>
							<p className="mt-[8px] text-[16px] text-white/70 leading-[100%]">
								{t('movingFundsTo', {
									platform: toPlatform === 'kalshi' ? 'Kalshi' : 'Polymarket',
								})}
							</p>
						</div>
						<p className=" text-[14px] text-white/50 leading-[100%]">{t('confirmWalletPrompts')}</p>
					</div>
				) : status === 'success' && quote ? (
					/* Success View */
					<div className="flex flex-col items-center gap-[16px] py-[32px]">
						<CheckCircle2 className="h-12 w-12 text-emerald-500" />
						<div className="text-center">
							<p className=" font-medium text-[18px] text-white leading-[100%]">
								{t('bridgeComplete')}
							</p>
							<p className="mt-[8px] text-[16px] text-white/70 leading-[100%]">
								{t('transferredTo', {
									amount: quote.estimatedOutput,
									platform: toPlatform === 'kalshi' ? 'Kalshi' : 'Polymarket',
								})}
							</p>
						</div>
						{txHash && (
							<a
								href={getExplorerUrl(
									quote.direction === 'toKalshi' ? CHAIN_CONFIG.polygon.id : CHAIN_CONFIG.solana.id,
									txHash
								)}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-[4px] rounded-[8px] bg-white/5 px-[12px] py-[6px] text-[12px] text-brand-blue hover:bg-white/10"
							>
								{t('viewOnExplorer')} <ExternalLink className="h-3 w-3" />
							</a>
						)}
						<Button
							onClick={handleClose}
							className="mt-[8px] h-12 w-full rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90"
						>
							{t('done')}
						</Button>
					</div>
				) : null}
			</DialogContent>
		</Dialog>
	)
}
